/**
 * Application event bus.
 *
 * A tiny, framework-agnostic publish/subscribe emitter used for cross-component
 * events (currently the left-menu show/hide/toggle signals). It deliberately
 * does NOT use `new Vue()` as an event hub.
 *
 * Why this exists (Vue 3 forward-compatibility):
 *   In Vue 2 it is common to use `new Vue()` purely as an event emitter via
 *   `$on`/`$emit`. That instance-as-emitter API is REMOVED in Vue 3, so any
 *   such bus becomes a hard migration blocker. This module replaces it with a
 *   self-contained emitter that has no dependency on Vue at all, so the Vue 3
 *   migration needs zero changes here. The public API (`on`/`off`/`emit`)
 *   intentionally mirrors `mitt`, the library the Vue team recommends as the
 *   Vue 3 replacement — if we ever want mitt, it is a drop-in swap behind this
 *   same surface.
 *
 * Behavior is identical to the previous `new Vue()` bus for our usage:
 * register handlers with `on`, fire with `emit(type, payload)`.
 */
class Emitter {
  constructor() {
    /** @type {Map<string, Function[]>} */
    this._handlers = new Map();
  }

  on(type, handler) {
    const handlers = this._handlers.get(type);
    if (handlers) {
      handlers.push(handler);
    } else {
      this._handlers.set(type, [handler]);
    }
  }

  off(type, handler) {
    const handlers = this._handlers.get(type);
    if (!handlers) return;
    if (!handler) {
      this._handlers.delete(type);
      return;
    }
    this._handlers.set(
      type,
      handlers.filter(h => h !== handler)
    );
  }

  emit(type, payload) {
    const handlers = this._handlers.get(type);
    if (!handlers) return;
    // Copy first so handlers that unsubscribe during dispatch don't skip peers.
    handlers.slice().forEach(handler => handler(payload));
  }
}

export const eventBus = new Emitter();

export default eventBus;
